import { GetAISettings, RemoveAISettings, SetAISettings } from "@/services/Cookies/cookies";

export interface AIMessage {
	id: string;
	type: "human" | "ai" | string;
	content: string;
}

export const getUserType = (userId: string): "student" | "professional" | null => {
	const cached = localStorage.getItem(`userInfo_${userId}`);
	if (cached && JSON.parse(cached)?.professionType !== undefined) {
		return JSON.parse(cached).professionType === 1 ? "student" : "professional";
	}
	return null;
};

// UserInfo localStorage functions
export const getUserInfo = (userId: string): any | null => {
	try {
		const cached = localStorage.getItem(`userInfo_${userId}`);
		if (cached) {
			return JSON.parse(cached);
		}
		return null;
	} catch (error) {
		console.error("Error parsing userInfo from localStorage:", error);
		return null;
	}
};

export const setUserInfo = (userId: string, userInfo: any): void => {
	try {
		localStorage.setItem(`userInfo_${userId}`, JSON.stringify(userInfo));

		// dispatch userInfoUpdated event
		window.dispatchEvent(new Event("userInfoUpdated"));
	} catch (error) {
		console.error("Error saving userInfo to localStorage:", error);
	}
};

export const removeUserInfo = (userId: string): void => {
	localStorage.removeItem(`userInfo_${userId}`);
};

// Resume context localStorage functions
export const getResumeContext = (resumeId: string): any | null => {
	try {
		const cached = localStorage.getItem(`resumeContext_${resumeId}`);
		if (cached) {
			return JSON.parse(cached);
		}
		return null;
	} catch (error) {
		console.error("Error parsing resumeContext from localStorage:", error);
		return null;
	}
};

export const setResumeContext = (resumeId: string, context: any): void => {
	try {
		localStorage.setItem(`resumeContext_${resumeId}`, JSON.stringify(context));
	} catch (error) {
		console.error("Error saving resumeContext to localStorage:", error);
	}
};

export const removeResumeContext = (resumeId: string): void => {
	localStorage.removeItem(`resumeContext_${resumeId}`);
};

// AI Settings localStorage functions
export interface AISettings {
	provider: string;
	model: string;
	apiKey: string;
	iv?: string; // Initialization vector for encryption
}

// Note: AI Settings are now stored in httpOnly cookies for security
// These functions are kept for backward compatibility but redirect to API calls
export const getAISettings = async (): Promise<AISettings> => {
	try {
		// Call API to get settings from secure cookie
		const response = await GetAISettings();

		return response;
	} catch (error) {
		console.error("Error fetching AI settings:", error);
		return {
			provider: "gemini",
			model: "gemini-3.1-flash-lite",
			apiKey: "",
		};
	}
};

export const setAISettings = async (settings: AISettings): Promise<void> => {
	try {
		// Call API to set settings in secure cookie
		await SetAISettings(settings);

		window.dispatchEvent(new Event("aiSettingsUpdated"));
	} catch (error) {
		console.error("Error saving AI settings:", error);
	}
};

export const removeAISettings = async (): Promise<void> => {
	try {
		// Call API to remove settings from secure cookie
		await RemoveAISettings();

		window.dispatchEvent(new Event("aiSettingsUpdated"));
	} catch (error) {
		console.error("Error removing AI settings:", error);
	}
};

// Chat messages localStorage functions
export const getChatMessages = (sessionId: string): AIMessage[] | null => {
	try {
		const cached = localStorage.getItem(`chatMessages_${sessionId}`);
		if (cached) {
			return JSON.parse(cached);
		}
		return null;
	} catch (error) {
		console.error("Error parsing chat messages from localStorage:", error);
		return null;
	}
};

export const setChatMessages = (sessionId: string, messages: AIMessage[]): void => {
	try {
		localStorage.setItem(`chatMessages_${sessionId}`, JSON.stringify(messages));
	} catch (error) {
		console.error("Error saving chat messages to localStorage:", error);
	}
};

export const removeChatMessages = (sessionId: string): void => {
	localStorage.removeItem(`chatMessages_${sessionId}`);
};
